<template>
  <div class="main">
    <header v-if="headerShown()" class="header">
      <div v-if="!shellOwnsTitle" class="title"><slot name="title" /></div>
      <div class="actions"><slot name="actions" /></div>
    </header>
    <div v-if="hasFilters" class="filters" :class="{ filtersTight: tight }"><slot name="filters" /></div>
    <section class="content">
      <div
        ref="body"
        class="contentBody"
        :class="{ contentBodyNoHeader: !headerShown(), contentBodyFill: fill, contentBodyTight: tight }"
      >
        <slot />
      </div>
    </section>
    <div v-if="hasFooter" class="footer" :class="{ footerTight: tight }"><slot name="footer" /></div>
  </div>
</template>

<script setup lang="ts">
import { Comment, Fragment, Text, computed, ref, useSlots, type VNode } from 'vue'
import { useShellOwnsTitle } from '../composables/shellTitle'

const props = defineProps({
  showHeader: { type: Boolean, default: true },
  fill: { type: Boolean, default: false },
  tight: { type: Boolean, default: false },
})

const slots = useSlots()
const hasFilters = computed(() => !!slots.filters)
const hasFooter = computed(() => !!slots.footer)
const shellOwnsTitle = useShellOwnsTitle()
// Under a shell that prints the title, the header only earns its row for actions.
function renders(nodes: VNode[]): boolean {
  return nodes.some((n) => {
    if (n.type === Comment) return false
    if (n.type === Text) return String(n.children ?? '').trim() !== ''
    if (n.type === Fragment) return Array.isArray(n.children) && renders(n.children as VNode[])
    return true
  })
}

const headerShown = () => props.showHeader && (!shellOwnsTitle || renders(slots.actions?.() ?? []))

// The scroll container, for pages that track or drive their own scroll position.
const body = ref<HTMLElement | null>(null)
defineExpose({ body })
</script>

<style scoped>
/* Page column inside the persistent AppShell. AppShell renders
   <router-view /> directly inside its body row, so this component's
   root acts as the flex item between the chat panel and the drawer. */
.main {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-5) var(--space-8) var(--space-3);
  flex-shrink: 0;
}
.title { display: inline-flex; align-items: center; min-width: 0; }
.actions { display: inline-flex; align-items: center; gap: var(--space-3); margin-left: auto; }

.filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: 0 var(--space-8) var(--space-3);
  flex-shrink: 0;
}

.content {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}
/* --content-pad-top is readable by sticky children (they rest below the padding). */
.contentBody {
  --content-pad-top: var(--space-3);
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: var(--content-pad-top) var(--space-8) var(--space-5);
}
.contentBody.contentBodyNoHeader {
  --content-pad-top: var(--space-5);
}
/* See the `fill` prop. min-height:0 is what lets a child actually shrink to the
   available height rather than being floored by its content. */
.contentBody.contentBodyFill {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
/* See the `tight` prop. The top override has to come after contentBodyNoHeader,
   which pads *up* to compensate for a missing header — the opposite of what a
   tight page wants. */
.contentBody.contentBodyTight {
  --content-pad-top: var(--space-4);
  padding-left: var(--space-4);
  padding-right: var(--space-4);
  /* No bottom gutter: a tight page's content runs to the fold, so trailing
     padding just floats the panel above the edge. */
  padding-bottom: 0;
}
.filters.filtersTight { padding-left: var(--space-4); padding-right: var(--space-4); }

.footer {
  flex-shrink: 0;
  padding: var(--space-2) var(--space-8);
  border-top: 1px solid var(--color-border-subtle);
}
.footer.footerTight { padding-left: var(--space-4); padding-right: var(--space-4); }
</style>
